import { useEffect, useMemo, useState } from "react";
import { Loader2, AlertTriangle, Coins } from "lucide-react";

import { useAppSelector } from "@/store/hooks";

import type { Wallet } from "@/interfaces/wallet";
import type { WalletAsset } from "@/interfaces/asset";

import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";

import { toast } from "sonner";
import SelectedAssetCard from "@/components/shared/swap/SelectedAssetCard";
import AssetSelector from "@/components/shared/swap/AssetSelector";
import TransactionSuccessDialog from "@/components/shared/TransactionSuccessDialog";

interface MintPayload {
  assetId: string;
  networkId: string;
  amount: string;
  otp: string;
  securityCode: string;
}

interface Props {
  wallet: Wallet;
  assets: WalletAsset[];
  initialAsset?: WalletAsset;
  isLoading: boolean;
  onMint: (payload: MintPayload) => Promise<void>;
  onSelectedAssetChange: (asset: WalletAsset) => void;
}

interface MintState {
  asset?: WalletAsset;
  amount: string;
}

const MintContainer = ({
  wallet,
  assets,
  initialAsset,
  isLoading,
  onMint,
  onSelectedAssetChange,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  const [mint, setMint] = useState<MintState>({
    asset: undefined,
    amount: "",
  });

  const [verificationOpen, setVerificationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successAsset, setSuccessAsset] = useState("");

  useEffect(() => {
    if (mint.asset) return;

    if (initialAsset) {
      setMint((prev) => ({
        ...prev,
        asset: initialAsset,
      }));

      return;
    }

    if (!assets.length) return;

    setMint((prev) => ({
      ...prev,
      asset: assets[0],
    }));
  }, [assets, initialAsset, mint.asset]);

  const updateAmount = (value: string) => {
    if (!/^\d*\.?\d*$/.test(value)) return;

    setMint((prev) => ({
      ...prev,
      amount: value,
    }));
  };

  const mintAmount = useMemo(() => {
    const amt = Number(mint.amount);

    return Number.isFinite(amt) ? amt : 0;
  }, [mint.amount]);

  const validationError = useMemo(() => {
    if (!mint.asset) {
      return "Select an asset.";
    }

    if (!mint.amount.trim()) {
      return "Enter amount to mint.";
    }

    if (mintAmount <= 0) {
      return "Enter a valid amount.";
    }

    return null;
  }, [mint]);

  const canMint = !validationError && !verificationOpen && !isLoading;

  const executeMint = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    if (!mint.asset) return;

    try {
      await onMint({
        assetId: mint.asset.id,
        networkId: mint.asset.networkId,
        amount: mint.amount,
        otp,
        securityCode,
      });

      setSuccessAsset(mint.asset.token.name);

      setSuccessOpen(true);

      setMint((prev) => ({
        ...prev,
        amount: "",
      }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? "Mint failed");
    }
  };

  return (
    <div data-lenis-prevent className="w-full bg-black/50 overflow-y-auto">
      <div className="w-full p-1 sm:p-4 md:p-6">
        <div
          className={`rounded-xl lg:max-w-3xl mx-auto w-full ${
            theme === "dark" ? "bg-card" : "bg-white"
          }`}
        >
          {/* HEADER */}

          <div
            className={`flex items-center justify-between p-6 border-b ${
              theme === "dark"
                ? "border-border bg-card"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0ECB81]/20 flex items-center justify-center">
                <Coins className="w-5 h-5 text-emerald-500" />
              </div>

              <div>
                <h3
                  className={`text-lg font-semibold ${
                    theme === "dark" ? "text-foreground" : "text-gray-900"
                  }`}
                >
                  Mint Asset
                </h3>

                <p
                  className={`text-sm ${
                    theme === "dark" ? "text-muted-foreground" : "text-gray-500"
                  }`}
                >
                  Create new token supply
                </p>
              </div>
            </div>
          </div>

          {/* BODY */}

          <div className="p-6 space-y-5">
            <div>
              <label className="text-sm text-muted-foreground">Asset</label>

              <SelectedAssetCard asset={mint.asset} />

              <div className="mt-2 text-xs text-muted-foreground flex justify-between">
                <span>Blockchain</span>

                <span className="font-medium text-foreground">
                  {mint.asset?.network?.name}
                </span>
              </div>

              <AssetSelector
                assets={assets}
                value={mint.asset}
                onChange={(asset) => {
                  setMint((prev) => ({
                    ...prev,
                    asset,
                  }));

                  onSelectedAssetChange(asset);
                }}
                label="Mint Asset"
                placeholder="Search asset..."
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Amount</label>

              <div className="relative">
                <input
                  value={mint.amount}
                  onChange={(e) => updateAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    theme === "dark"
                      ? "bg-muted border-border text-foreground"
                      : "bg-white border-gray-200"
                  }`}
                />

                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {mint.asset?.token.symbol}
                </span>
              </div>
            </div>

            <div className="rounded-lg bg-yellow-500/10 p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mt-1" />

              <p className="text-sm text-yellow-500">
                Minting creates new blockchain tokens and increases the
                circulating supply. Ensure the amount has been verified before
                approval.
              </p>
            </div>

            {validationError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <p className="text-sm text-red-500">{validationError}</p>
              </div>
            )}

            <button
              disabled={!canMint}
              onClick={() => {
                if (!canMint) return;

                setVerificationOpen(true);
              }}
              className={`w-full py-4 rounded-lg font-semibold ${
                canMint
                  ? "bg-[#0ECB81] text-black"
                  : "bg-[#0ECB81] text-black opacity-50 cursor-not-allowed"
              }`}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                "Mint Asset"
              )}
            </button>
          </div>
        </div>
      </div>

      <AdminVerificationDialog
        open={verificationOpen}
        onOpenChange={setVerificationOpen}
        systemType={wallet.systemType}
        onSuccess={executeMint}
      />

      <TransactionSuccessDialog
        open={successOpen}
        onOpenChange={setSuccessOpen}
        title="Mint Successful"
        message={`${successAsset} minted successfully.`}
      />
    </div>
  );
};

export default MintContainer;
